import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Fish, Droplets, Hand, Sparkles, Drumstick, Lightbulb, MoreHorizontal, X, BookOpen, ShoppingBag, Flower2, Save, Settings, HelpCircle, Image, Music, Volume2, VolumeX } from 'lucide-react'

interface ActionBarMobileProps {
  coins: number
  onOpenFishDrawer: () => void
  onFeed: () => void
  onClean: () => void
  onTease: () => void
  onToggleLight: () => void
  onLightShow: () => void
  lighting: 'day' | 'night'
  onOpenCodex: () => void
  onOpenShop: () => void
  onOpenDecoration: () => void
  onOpenBackground: () => void
  onOpenAmbient: () => void
  onSave: () => void
  soundEnabled: boolean
  onToggleSound: () => void
}

export function ActionBarMobile({ coins, onOpenFishDrawer, onFeed, onClean, onTease, onToggleLight, onLightShow, lighting, onOpenCodex, onOpenShop, onOpenDecoration, onOpenBackground, onOpenAmbient, onSave, soundEnabled, onToggleSound }: ActionBarMobileProps) {
  const [moreOpen, setMoreOpen] = useState(false)

  const handleMoreAction = (action: () => void) => {
    action()
    setMoreOpen(false)
  }

  const mainButtons = [
    { icon: Fish, label: '切换', onClick: onOpenFishDrawer, color: 'text-sky-500' },
    { icon: Drumstick, label: '喂食', onClick: onFeed, color: 'text-amber-500' },
    { icon: Droplets, label: '清洁', onClick: onClean, color: 'text-blue-500' },
    { icon: Lightbulb, label: lighting === 'day' ? '夜灯' : '日灯', onClick: onToggleLight, color: lighting === 'night' ? 'text-amber-500' : 'text-slate-500' },
    { icon: Hand, label: '逗鱼', onClick: onTease, color: 'text-pink-500' },
    { icon: Sparkles, label: '光影', onClick: onLightShow, color: 'text-purple-500' },
  ]

  const soundButton = {
    icon: soundEnabled ? Volume2 : VolumeX,
    label: soundEnabled ? '静音' : '开启音效',
    onClick: onToggleSound,
    color: soundEnabled ? 'text-emerald-500' : 'text-slate-400',
  }

  const moreButtons = [
    { icon: BookOpen, label: '图鉴', onClick: onOpenCodex },
    { icon: ShoppingBag, label: '商店', onClick: onOpenShop },
    { icon: Flower2, label: '装饰', onClick: onOpenDecoration },
    { icon: Image, label: '背景', onClick: onOpenBackground },
    { icon: Music, label: '音效', onClick: onOpenAmbient },
    { icon: Save, label: '保存', onClick: onSave },
    { icon: Settings, label: '设置', onClick: () => {} },
    { icon: HelpCircle, label: '帮助', onClick: () => {} },
  ]

  return (
    <>
      <AnimatePresence>
        {moreOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMoreOpen(false)}
            />
            <motion.div
              className="fixed bottom-[80px] left-4 right-4 z-50 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200 shadow-xl"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between p-3 border-b border-slate-100">
                <span className="text-xs font-bold tracking-widest text-slate-400">更多操作</span>
                <button
                  onClick={() => setMoreOpen(false)}
                  className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"
                >
                  <X size={14} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 p-3">
                {moreButtons.map(({ icon: Icon, label, onClick }) => (
                  <button
                    key={label}
                    onClick={() => handleMoreAction(onClick)}
                    className="flex flex-col items-center gap-1 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <Icon size={22} className="text-slate-600" />
                    <span className="text-xs font-medium text-slate-600">{label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="fixed bottom-4 left-4 right-4 z-30">
        <div className="flex items-center justify-between rounded-2xl bg-white/90 backdrop-blur-xl border border-white/80 px-2 py-1.5 shadow-[0_10px_36px_rgba(70,140,220,0.22)]">
          <div className="flex items-center gap-1.5 px-2.5">
            <span className="text-base">💰</span>
            <span className="font-black text-amber-600 tabular-nums text-sm">{coins}</span>
          </div>

          <div className="flex items-center gap-0.5">
            {mainButtons.map(({ icon: Icon, label, onClick, color }) => (
              <button
                key={label}
                onClick={onClick}
                className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/70 border border-white/80 hover:bg-white transition-all active:scale-90"
                title={label}
              >
                <Icon size={18} className={color} />
              </button>
            ))}
            <button
              onClick={soundButton.onClick}
              className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/70 border border-white/80 hover:bg-white transition-all active:scale-90"
              title={soundButton.label}
            >
              {soundButton.icon === Volume2 ? <Volume2 size={18} className={soundButton.color} /> : <VolumeX size={18} className={soundButton.color} />}
            </button>
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all active:scale-90 ${
                moreOpen ? 'bg-sky-500 border-sky-400' : 'bg-white/70 border-white/80 hover:bg-white'
              }`}
              title="更多"
            >
              <MoreHorizontal size={18} className={moreOpen ? 'text-white' : 'text-slate-500'} />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
